import React, { useEffect, useMemo, useRef, useState } from "react";
import { Paper, Box, Typography, Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

import { useAuthContext } from "../../../context/AuthContext";
import { useCategories } from "../../../hooks/useCategories";
import { useCompanies } from "../../../hooks/useCompanies";
import { useS3Upload } from "../../../hooks/useS3Upload";
import { formatCityLabel } from "./CityAutocomplete";

import TrechoContentSection from "./sections/TrechoContentSection";
import TrechoPriceSection from "./sections/TrechoPriceSection";
import TrechoExtrasSection from "./sections/TrechoExtrasSection";
import GallerySection from "../AddProductForm/sections/GallerySection";
import StatusHighlightSection from "../AddProductForm/sections/StatusHighlightSection";
import ActionBar from "../AddProductForm/sections/ActionBar";

const genId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const MAX_FILE_MB = 10;

function buildDefaultNome(origem, destino) {
  const o = formatCityLabel(origem);
  const d = formatCityLabel(destino);
  if (!o || !d) return "";
  return `${o} para ${d}`;
}

function cityFromProduct(product, kind) {
  const embedded = kind === "origem" ? product?.origem_cidade : product?.destino_cidade;
  if (embedded?.id) return embedded;
  const id = kind === "origem" ? product?.id_origem : product?.id_destino;
  const nome = kind === "origem" ? product?.origem_nome : product?.destino_nome;
  if (!id) return null;
  const city = { id: Number(id) };
  if (nome && String(nome).trim() && String(nome) !== String(id)) {
    city.nome = String(nome).trim();
  }
  return city;
}

export default function AddTrechoForm({
  product,
  onClose,
  onSubmit,
  isFetching,
  defaultProjectId = null,
}) {
  const theme = useTheme();
  const { userData } = useAuthContext();
  const { t } = useTranslation("products");
  const fileInputRef = useRef(null);
  const nomeManualRef = useRef(Boolean(product?.id && product?.nome));

  const [errorMessage, setErrorMessage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [origemCity, setOrigemCity] = useState(() => cityFromProduct(product, "origem"));
  const [destinoCity, setDestinoCity] = useState(() => cityFromProduct(product, "destino"));

  const getDefaultIdsProjetos = () => {
    if (product?.ids_projetos != null) return product.ids_projetos;
    if (defaultProjectId != null) return defaultProjectId;
    return null;
  };

  const {
    rootCategories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
    error: categoriesErrorObj,
  } = useCategories({ page: 1, limit: 500 });

  const {
    data: companiesQuery = { companies: [] },
    isLoading: companiesLoading,
    isError: companiesError,
    error: companiesErrorObj,
  } = useCompanies(1, 500, "");

  const companies = companiesQuery?.companies || [];

  const [formData, setFormData] = useState(() => ({
    ...product,
    ids_projetos: getDefaultIdsProjetos(),
    tipo: 5,
    id_categoria: product?.id_categoria ?? null,
    id_subcategoria: product?.id_subcategoria ?? null,
    nome: product?.nome ?? "",
    sku: product?.sku ?? "",
    tags: product?.tags ?? "",
    fonte: product?.fonte ?? "",
    id_origem: product?.id_origem ?? null,
    id_destino: product?.id_destino ?? null,
    preco: Number.isFinite(+product?.preco) ? +product.preco : "",
    preco_comdesconto: Number.isFinite(+product?.preco_comdesconto) ? +product.preco_comdesconto : "",
    max_desconto: Number.isFinite(+product?.max_desconto) ? +product.max_desconto : "",
    estoque: 0,
    detalhes: product?.detalhes ?? "",
    aceitacarga: product?.aceitacarga ? 1 : 0,
    distancia: product?.distancia ?? "",
    status: product?.status == null ? 1 : Number(product.status),
    destaque: product?.destaque ? 1 : 0,
    galeria: Array.isArray(product?.galeria)
      ? [...product.galeria].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
      : product?.imagem
      ? [{ id: genId(), url: product.imagem, ordem: 1, isCover: true }]
      : [],
    id_usuario: product?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
  }));

  const { uploadMany: uploadManyReal } = useS3Upload();

  useEffect(() => {
    if (!product?.id) return;
    nomeManualRef.current = true;
    setOrigemCity(cityFromProduct(product, "origem"));
    setDestinoCity(cityFromProduct(product, "destino"));
    setFormData((prev) => ({
      ...prev,
      ...product,
      tipo: 5,
      ids_projetos: product?.ids_projetos ?? null,
      fonte: product?.fonte ?? "",
      id_origem: product?.id_origem ?? null,
      id_destino: product?.id_destino ?? null,
      aceitacarga: product?.aceitacarga ? 1 : 0,
      distancia: product?.distancia ?? "",
      estoque: 0,
      status: product?.status == null ? 1 : Number(product.status),
      destaque: product?.destaque ? 1 : 0,
      galeria: Array.isArray(product?.galeria)
        ? [...product.galeria].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
        : product?.imagem
        ? [{ id: genId(), url: product.imagem, ordem: 1, isCover: true }]
        : [],
    }));
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      formData.galeria?.forEach((img) => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onAddFiles = (ev) => addFiles(ev.detail || []);
    window.addEventListener("gallery:addFiles", onAddFiles);
    return () => window.removeEventListener("gallery:addFiles", onAddFiles);
  }, [formData.galeria]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nomeManualRef.current) return;
    const auto = buildDefaultNome(origemCity, destinoCity);
    if (!auto) return;
    setFormData((p) => (p.nome === auto ? p : { ...p, nome: auto }));
  }, [origemCity, destinoCity]);

  const selectedCategory = useMemo(
    () => rootCategories.find((c) => String(c.id) === String(formData.id_categoria)),
    [rootCategories, formData.id_categoria]
  );
  const subcats = selectedCategory?.children || [];

  const precoBase = Number(formData.preco) || 0;
  const precoFinal =
    formData.preco_comdesconto !== "" && formData.preco_comdesconto !== null
      ? Number(formData.preco_comdesconto)
      : precoBase;
  const economia = precoBase > 0 ? precoBase - precoFinal : 0;
  const economiaPct = precoBase > 0 ? (economia / precoBase) * 100 : 0;
  const precoPromoValido =
    formData.preco_comdesconto === "" ||
    formData.preco_comdesconto === null ||
    Number(formData.preco_comdesconto) >= 0;

  const handleText = (e) => {
    const { name, value } = e.target;
    if (name === "nome") nomeManualRef.current = true;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSelect = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: Number(value) }));
  };

  const handleSwitch = (e) => {
    const { name, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: checked ? 1 : 0 }));
  };

  const handleProjectChange = (_evt, companyOption) => {
    setFormData((prev) => ({ ...prev, ids_projetos: companyOption ? companyOption.id : null }));
  };

  const handleOrigemChange = (city) => {
    setOrigemCity(city);
    setFormData((p) => ({ ...p, id_origem: city?.id ?? null }));
  };

  const handleDestinoChange = (city) => {
    setDestinoCity(city);
    setFormData((p) => ({ ...p, id_destino: city?.id ?? null }));
  };

  const validateAndNormalizeFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return [];
    const accepted = [];
    const errors = [];
    for (const f of files) {
      const typeStr = f?.type ? String(f.type) : "";
      const isImage = /^image\//i.test(typeStr);
      const sizeOk = f?.size != null ? f.size <= MAX_FILE_MB * 1024 * 1024 : false;
      const fileWord = t("form.fileWord");
      if (!isImage) {
        errors.push(`${f?.name || fileWord}: ${t("form.fileErrorInvalidType")}`);
      } else if (!sizeOk) {
        errors.push(`${f?.name || fileWord}: ${t("form.fileErrorTooLarge", { mb: MAX_FILE_MB })}`);
      } else {
        accepted.push(f);
      }
    }
    if (errors.length) {
      setErrorMessage(t("form.skippedFiles", { details: errors.join(" ") }));
      setTimeout(() => setErrorMessage(null), 4000);
    }
    return accepted;
  };

  const addFiles = (fileList) => {
    const files = validateAndNormalizeFiles(fileList);
    if (!files.length) return;
    setFormData((prev) => {
      const base = [...(prev.galeria || [])];
      let ordemStart = base.length ? Math.max(...base.map((i) => i.ordem || 0)) + 1 : 1;
      const newImgs = files.map((f, idx) => ({
        id: genId(),
        file: f,
        preview: URL.createObjectURL(f),
        ordem: ordemStart + idx,
        isCover: base.length === 0 && idx === 0,
      }));
      return { ...prev, galeria: [...base, ...newImgs] };
    });
  };

  const removeImage = (idx) => {
    setFormData((prev) => {
      const list = [...prev.galeria];
      const removed = list.splice(idx, 1)[0];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      if (!list.some((i) => i.isCover) && list[0]) list[0].isCover = true;
      list.forEach((img, i) => {
        img.ordem = i + 1;
      });
      return { ...prev, galeria: list };
    });
  };

  const moveImage = (idx, dir) => {
    setFormData((prev) => {
      const list = [...prev.galeria];
      const to = idx + dir;
      if (to < 0 || to >= list.length) return prev;
      const [it] = list.splice(idx, 1);
      list.splice(to, 0, it);
      list.forEach((img, i) => {
        img.ordem = i + 1;
      });
      return { ...prev, galeria: list };
    });
  };

  const makeCover = (idx) => {
    setFormData((prev) => ({
      ...prev,
      galeria: prev.galeria.map((i, iidx) => ({ ...i, isCover: iidx === idx })),
    }));
  };

  const buildPrefix = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const workspace = userData?.id_workspace || "default";
    return `workspace-${workspace}/products/${yyyy}/${mm}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!formData.id_origem || !formData.id_destino) {
      setErrorMessage(t("trechoForm.errorOriginDestination"));
      return;
    }
    if (String(formData.id_origem) === String(formData.id_destino)) {
      setErrorMessage(t("trechoForm.errorSameCity"));
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress({ done: 0, total: 0 });

      const novos = (formData.galeria || []).filter((g) => g.file);
      const uploadedById = new Map();

      if (novos.length) {
        const results = await uploadManyReal(
          novos.map((n) => n.file),
          { prefix: buildPrefix() },
          (done, total) => setUploadProgress({ done, total })
        );
        results.forEach((r, i) => {
          const ref = novos[i];
          if (r?.ok) uploadedById.set(ref.id, r.url);
          else {
            throw new Error(
              t("form.uploadFailedFor", {
                name: ref.file?.name,
                error: r?.error || t("form.unknownUploadError"),
              })
            );
          }
        });
      }

      const num = (v) => (v === "" || v == null ? null : Number(v));
      const galeriaFinal = (formData.galeria || [])
        .map(({ id, url, ordem, isCover }) => ({
          id: id && String(id).startsWith("tmp_") ? undefined : id,
          url: uploadedById.get(id) || url || undefined,
          ordem,
          isCover: !!isCover,
        }))
        .filter((g) => !!g.url);

      const cover =
        galeriaFinal.find((i) => i.isCover && i.url) || galeriaFinal.find((i) => i.url);

      const payload = {
        id: formData.id,
        ids_projetos: formData.ids_projetos ?? null,
        id_usuario: formData.id_usuario || userData?.id,
        id_usuario_ultima_alteracao: userData?.id,
        tipo: 5,
        nome: formData.nome?.trim() || buildDefaultNome(origemCity, destinoCity) || "",
        sku: (formData.sku || "").trim(),
        tags: (formData.tags || "").trim() || null,
        fonte: (formData.fonte || "").trim() || null,
        id_origem: num(formData.id_origem),
        id_destino: num(formData.id_destino),
        preco: num(formData.preco) ?? 0,
        preco_comdesconto: num(formData.preco_comdesconto),
        max_desconto: num(formData.max_desconto) ?? 0,
        estoque: 0,
        detalhes: formData.detalhes || "",
        aceitacarga: Number(formData.aceitacarga) === 1 ? 1 : 0,
        distancia: (formData.distancia || "").trim() || null,
        status: Number(formData.status) === 1 ? 1 : 0,
        destaque: Number(formData.destaque) === 1 ? 1 : 0,
        id_categoria: formData.id_categoria ?? null,
        id_subcategoria: formData.id_subcategoria ?? null,
        imagem: cover?.url || null,
        galeria: galeriaFinal,
      };

      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(err?.message || t("form.errorS3Upload"));
    } finally {
      setIsUploading(false);
      setUploadProgress({ done: 0, total: 0 });
    }
  };

  const categoriesAlert = categoriesError
    ? categoriesErrorObj?.message || t("form.errorLoadCategories")
    : null;
  const companiesAlert = companiesError
    ? companiesErrorObj?.message || t("form.errorLoadCompanies")
    : null;

  const formValid =
    formData.ids_projetos &&
    formData.nome?.trim() &&
    formData.id_origem &&
    formData.id_destino &&
    String(formData.id_origem) !== String(formData.id_destino) &&
    precoPromoValido;

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      elevation={3}
      sx={{
        p: 2,
        width: "95%",
        m: "0 auto",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))"
            : "linear-gradient(180deg, #fff, #fafafa)",
      }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        {product?.id ? t("trechoForm.titleEdit") : t("trechoForm.titleAdd")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("trechoForm.intro")}
      </Typography>

      {(errorMessage || categoriesAlert || companiesAlert) && (
        <Box sx={{ mb: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {categoriesAlert && <Alert severity="warning">{categoriesAlert}</Alert>}
          {companiesAlert && <Alert severity="warning">{companiesAlert}</Alert>}
        </Box>
      )}

      <TrechoContentSection
        companies={companies}
        companiesLoading={companiesLoading}
        formData={formData}
        handleProjectChange={handleProjectChange}
        categories={rootCategories}
        categoriesLoading={categoriesLoading}
        subcats={subcats}
        handleText={handleText}
        handleCategoryChange={(e, option) =>
          setFormData((p) => ({
            ...p,
            id_categoria: option ? option.id : null,
            id_subcategoria: null,
          }))
        }
        handleSubcatChange={(e, option) =>
          setFormData((p) => ({ ...p, id_subcategoria: option ? option.id : null }))
        }
        origemCity={origemCity}
        destinoCity={destinoCity}
        onOrigemChange={handleOrigemChange}
        onDestinoChange={handleDestinoChange}
      />

      <TrechoPriceSection
        formData={formData}
        handleText={handleText}
        precoBase={precoBase}
        precoFinal={precoFinal}
        economia={economia}
        economiaPct={economiaPct}
        descontoValido={precoPromoValido}
      />

      <TrechoExtrasSection formData={formData} handleText={handleText} handleSelect={handleSelect} />

      <GallerySection
        formData={formData}
        fileInputRef={fileInputRef}
        handleClickAdd={() => fileInputRef.current?.click()}
        handleDrop={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          addFiles(ev.dataTransfer.files);
        }}
        handleDragOver={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        }}
        moveImage={moveImage}
        makeCover={makeCover}
        removeImage={removeImage}
      />

      <StatusHighlightSection formData={formData} handleSwitch={handleSwitch} />

      <ActionBar
        onClose={onClose}
        isFetching={isFetching || isUploading}
        disabled={isFetching || isUploading || !formValid}
        statusHint={
          isUploading && uploadProgress.total
            ? t("form.uploadProgress", {
                done: uploadProgress.done,
                total: uploadProgress.total,
              })
            : undefined
        }
      />
    </Paper>
  );
}
